import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BACKEND_URL } from '../../config';

const API = `${BACKEND_URL}/api`;
const ACHIEVEMENT_ID = 'tap-lock-code';

const TapLockCode = () => {
  const navigate = useNavigate();
  const [showOffer, setShowOffer] = useState(false);
  const [targetCode, setTargetCode] = useState('');
  const [currentCode, setCurrentCode] = useState('');
  const [locked, setLocked] = useState(false);
  const [statusText, setStatusText] = useState('');
  const intervalRef = useRef(null);

  useEffect(() => {
    // Generate random 6-digit code
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setTargetCode(code);
    startRolling();

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const startRolling = () => {
    intervalRef.current = setInterval(() => {
      setCurrentCode(Math.floor(100000 + Math.random() * 900000).toString());
    }, 100);
  };

  const handleLockIn = async () => {
    if (locked) return;
    
    clearInterval(intervalRef.current);
    setLocked(true);
    
    // Calculate how close the user was
    const diff = Math.abs(parseInt(currentCode) - parseInt(targetCode));
    const threshold = 5000;
    
    if (diff <= threshold) {
      setStatusText('HANDSHAKE LOCKED. Decryption sequence accepted.');
      
      // Store achievement in sessionStorage
      sessionStorage.setItem('pendingAchievement', ACHIEVEMENT_ID);

      setTimeout(() => {
        setShowOffer(true);
        setTimeout(() => {
          navigate(`/scan?achievement=${ACHIEVEMENT_ID}`);
        }, 1300);
      }, 1100);
    } else {
      setStatusText('Sequence mismatch. System override engaged — granting access.');
      sessionStorage.setItem('pendingAchievement', ACHIEVEMENT_ID);
      setTimeout(() => {
        setShowOffer(true);
        setTimeout(() => {
          navigate(`/scan?achievement=${ACHIEVEMENT_ID}`);
        }, 1300);
      }, 1200);
    }
  };

  return (
    <div style={styles.wrap}>
      {!showOffer ? (
        <div style={styles.terminal}>
          <div style={styles.header}>
            <div style={styles.dots}>
              <span style={styles.dot}></span>
              <span style={styles.dot}></span>
              <span style={styles.dot}></span>
            </div>
            <div style={styles.headerLabel}>DECRYPTION HANDSHAKE PROTOCOL</div>
          </div>
          
          <div style={styles.body}>
            <div style={styles.line}>
              <span style={styles.label}>SYSTEM:</span>
              <span style={styles.value}> SECURE CHANNEL HANDSHAKE v2.1</span>
            </div>
            <div style={styles.line}>
              <span style={styles.label}>TARGET:</span>
              <span style={{...styles.value, color: '#fbbf24', fontSize: '24px', fontWeight: 700}}> {targetCode}</span>
            </div>
            <div style={styles.line}>
              <span style={styles.label}>STATUS:</span>
              <span style={{...styles.value, color: '#ef4444'}}> AWAITING HANDSHAKE LOCK</span>
            </div>
            
            <div style={styles.gameTitle}>Lock In The Decryption Code</div>
            <div style={styles.gameInstruction}>
              Tap <strong>LOCK IN</strong> when the rolling code matches (or gets close to) the target sequence.
            </div>
            
            <div style={styles.codeDisplay}>
              <div style={styles.rollingCode}>{currentCode}</div>
            </div>
            
            <button 
              style={{...styles.btn, ...(locked ? styles.btnDisabled : {})}}
              onClick={handleLockIn}
              disabled={locked}
            >
              {locked ? 'LOCKED' : 'LOCK IN'}
            </button>
            
            {statusText && (
              <div style={styles.statusText}>{statusText}</div>
            )}
          </div>
        </div>
      ) : (
        <div style={styles.offer}>
          <small style={{color: '#10b981', fontWeight: 700}}>HANDSHAKE COMPLETE</small>
          <div style={styles.offerTitle}>Decryption successful.</div>
          <p>You've locked in the sequence. Your <strong>exclusive Tappd offer</strong> awaits.</p>
          <p style={styles.tagline}>Opening secure file…</p>
        </div>
      )}
    </div>
  );
};

const styles = {
  wrap: { minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: '"Courier New", monospace' },
  terminal: { background: '#1e293b', borderRadius: '12px', maxWidth: '600px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid #334155' },
  header: { background: '#334155', padding: '12px 16px', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px' },
  dots: { display: 'flex', gap: '6px' },
  dot: { width: '12px', height: '12px', borderRadius: '50%', background: '#64748b' },
  headerLabel: { color: '#e2e8f0', fontSize: '14px', fontWeight: 700, letterSpacing: '1px' },
  body: { padding: '24px', color: '#10b981', fontSize: '14px', lineHeight: '1.8' },
  line: { marginBottom: '8px' },
  label: { color: '#fbbf24', fontWeight: 700 },
  value: { color: '#10b981' },
  gameTitle: { color: '#fbbf24', fontSize: '18px', fontWeight: 700, marginTop: '24px', marginBottom: '12px', textTransform: 'uppercase' },
  gameInstruction: { color: '#cbd5e1', fontSize: '14px', marginBottom: '24px' },
  codeDisplay: { background: '#0f172a', padding: '40px', borderRadius: '12px', border: '2px solid #334155', marginBottom: '24px', textAlign: 'center' },
  rollingCode: { fontSize: '48px', fontWeight: 700, color: '#10b981', letterSpacing: '4px', fontFamily: '"Courier New", monospace' },
  btn: { width: '100%', padding: '16px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 700, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '1px' },
  btnDisabled: { background: '#64748b', cursor: 'not-allowed' },
  statusText: { fontSize: '14px', fontWeight: 600, textAlign: 'center', marginTop: '16px', color: '#10b981' },
  offer: { maxWidth: '600px', background: 'white', padding: '40px', borderRadius: '12px', textAlign: 'center', color: '#1e293b' },
  offerTitle: { fontSize: '28px', fontWeight: 700, margin: '16px 0', fontFamily: 'Fraunces, serif' },
  tagline: { color: '#64748b', fontStyle: 'italic', marginTop: '16px' },
};

export default TapLockCode;